import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useEffect, useMemo, useState } from "react";
import { Alert } from "@material-ui/lab";
import { Snackbar } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import WarehouseService from "../../services/WarehouseService";
import DataTableProductsSell from "../../ui/DataTableProductsSell";
import withLoader from "../../components/layouts/WithLoader";

const animatedComponents = makeAnimated();

const PRODUCT_TYPES = [
  { value: "physique", label: "Produits physiques", icon: "fas fa-box", code: 1 },
  { value: "service", label: "Services", icon: "fas fa-concierge-bell", code: 2 },
];

const ProductSell = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState({ open: false, severity: "error", text: "" });
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [productStatus, setProductStatus] = useState("physique");
  const [listProduits, setListProduits] = useState([]);
  const [showTable, setShowTable] = useState(false);

  const showAlert = (text, severity = "error") =>
    setAlert({ open: true, severity, text });

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlert((a) => ({ ...a, open: false }));
  };

  useEffect(() => {
    WarehouseService.getAllRealWarehouseForPrice(getCurrentUserId(), 0)
      .then((response) => {
        if (response.data.success) {
          setWarehouses(response.data.success);
        } else {
          showAlert("Erreur : pas de dépôt");
        }
      })
      .catch((e) => {
        console.log("error in getAllRealWarehouse", e);
        showAlert("Erreur : impossible de charger les dépôts");
      });
  }, []);

  const warehouseOptions = useMemo(
    () =>
      warehouses
        .filter((w) => w?.id !== undefined)
        .map((w) => ({ value: w.id, label: w.name })),
    [warehouses]
  );

  const onChangeWarehouse = (option) => {
    setSelectedWarehouse(option);
    setShowTable(false);
    setListProduits([]);
  };

  const onChangeType = (value) => {
    setProductStatus(value);
    setShowTable(false);
    setListProduits([]);
  };

  const onGetProductsByWarehouse = () => {
    if (!selectedWarehouse) {
      showAlert("Veuillez choisir un dépôt", "warning");
      return;
    }
    const typeProduct = PRODUCT_TYPES.find((t) => t.value === productStatus)?.code ?? 0;

    startLoading();
    WarehouseService.getAllWarehouseByIdAndTypeProduct(
      getCurrentUserId(),
      selectedWarehouse.value,
      typeProduct
    )
      .then((response) => {
        if (response.data["success"]) {
          sessionStorage.setItem("idWarehouseSell", selectedWarehouse.value);
          setListProduits(response.data["success"].productStocks || []);
          setShowTable(true);
        } else {
          showAlert("Aucun résultat pour ce dépôt");
        }
      })
      .catch((e) => {
        console.log("error", e);
        showAlert("Erreur : impossible de charger les produits");
      })
      .finally(() => stopLoading());
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: "black" }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active" style={{ color: "black" }}>
                  Prix de vente
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-dark mt-3">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h3 className="card-title">
                <i className="fas fa-tags mr-2"></i>
                Définir le prix de vente des produits
              </h3>
              <button
                className="btn btn-sm btn-light"
                onClick={() => navigate("/uploadExcelProduct")}
              >
                <i className="fas fa-file-excel mr-1"></i> Importer un fichier Excel
              </button>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Dépôt</label>
                    <Select
                      closeMenuOnSelect={true}
                      components={animatedComponents}
                      value={selectedWarehouse}
                      onChange={onChangeWarehouse}
                      options={warehouseOptions}
                      placeholder="Choisir un dépôt..."
                      noOptionsMessage={() => "Aucun dépôt"}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Type de produit</label>
                    <div className="btn-group btn-block" role="group">
                      {PRODUCT_TYPES.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          className={`btn ${
                            productStatus === t.value ? "btn-dark" : "btn-outline-dark"
                          }`}
                          onClick={() => onChangeType(t.value)}
                        >
                          <i className={`${t.icon} mr-1`}></i> {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer text-center">
              <button
                className="btn btn-dark"
                onClick={onGetProductsByWarehouse}
                disabled={!selectedWarehouse}
              >
                <i className="fas fa-search mr-1"></i> Afficher les produits
              </button>
            </div>
          </div>

          {showTable && listProduits.length > 0 && (
            <>
              <div className="mb-2">
                <span className="badge badge-dark" style={{ fontSize: "0.85rem" }}>
                  {listProduits.length} produit{listProduits.length > 1 ? "s" : ""} —{" "}
                  {selectedWarehouse?.label}
                </span>
              </div>
              <DataTableProductsSell listProduits={listProduits} />
            </>
          )}
          {showTable && listProduits.length === 0 && (
            <div className="alert alert-info">
              Aucun produit trouvé dans ce dépôt pour ce type.
            </div>
          )}
        </div>
      </section>

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={handleAlertClose} severity={alert.severity}>
          {alert.text}
        </Alert>
      </Snackbar>
    </div>
  );
};
export default withLoader(ProductSell);
